import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import globalStyle from '../../styles/common.module.css'
import style from './pages.module.css'
import { CollectionsQuery } from '../../util/types'
import { MoreIcon } from './MoreIcon'

type CollectionItem = {
  id: string
  name: string
  slug?: string | null
  stories?: Array<{ id: string; title: string; slug?: string | null }> | null
}

type StoryListItemProps = {
  story: { id: string; title: string; slug?: string | null }
  index: number
  collectionSlug: string
  type: 'topic' | 'category'
}

function StoryListItem({ story, index, collectionSlug, type }: StoryListItemProps) {
  const navigate = useNavigate()
  
  const handleClick = () => {
    if (story.slug) {
      navigate(`/${type}/${collectionSlug}?excerpt=${story.slug}`)
    }
  }

  return (
    <li 
      className={style.storyListItem}
      onClick={handleClick}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          handleClick()
        }
      }}
    >
      <span className={style.storyNumber}>{index + 1}</span>
      <span className={style.storyTitle}>{story.title}</span>
      <RightArrowIcon />
    </li>
  )
}

function RightArrowIcon() {
  return (
    <svg
      width='15'
      height='14'
      viewBox='0 0 15 14'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      className={style.storyIcon}
    >
      <path
        d='M1 7.11389L12.9166 7.11389'
        stroke='currentColor'
        stroke-width='2'
        stroke-linecap='round'

      />
      <path

        d='M7.70711 0.792893C7.31658 0.402369 6.68342 0.402369 6.29289 0.792893C5.90237 1.18342 5.90237 1.81658 6.29289 2.20711L7.70711 0.792893ZM13.5 7.24612L14.1 8.04612C14.3319 7.87219 14.4769 7.60618 14.4975 7.31701C14.518 7.02785 14.4121 6.744 14.2071 6.53902L13.5 7.24612ZM6.20692 11.7209C5.7766 12.0671 5.70839 12.6965 6.05458 13.1269C6.40076 13.5572 7.03024 13.6254 7.46056 13.2792L6.20692 11.7209ZM6.29289 2.20711C7.3259 3.24012 8.73113 4.45168 9.97769 5.51029C11.2584 6.59786 12.3309 7.49126 12.7929 7.95323L14.2071 6.53902C13.6691 6.00099 12.4916 5.02133 11.2723 3.98583C10.0189 2.92139 8.6741 1.75988 7.70711 0.792893L6.29289 2.20711ZM12.9 6.44612C11.887 7.20588 10.2097 8.528 8.79463 9.65275C8.08514 10.2167 7.43837 10.7336 6.969 11.1095C6.73428 11.2975 6.54388 11.4502 6.4121 11.556C6.3462 11.6089 6.29497 11.6501 6.26018 11.6781C6.24278 11.692 6.22949 11.7027 6.22054 11.7099C6.21607 11.7135 6.21268 11.7162 6.2104 11.7181C6.20926 11.719 6.20839 11.7197 6.20781 11.7201C6.20752 11.7204 6.2073 11.7206 6.20715 11.7207C6.20708 11.7207 6.20702 11.7208 6.20698 11.7208C6.20694 11.7208 6.20692 11.7209 6.83374 12.5C7.46056 13.2792 7.46057 13.2792 7.4606 13.2792C7.46064 13.2791 7.46068 13.2791 7.46075 13.279C7.46089 13.2789 7.46109 13.2788 7.46137 13.2785C7.46192 13.2781 7.46275 13.2774 7.46386 13.2765C7.46608 13.2748 7.46942 13.2721 7.47383 13.2685C7.48266 13.2614 7.49582 13.2508 7.51309 13.237C7.54763 13.2092 7.5986 13.1683 7.6642 13.1156C7.79542 13.0102 7.98519 12.858 8.21919 12.6706C8.68724 12.2958 9.33203 11.7804 10.0391 11.2184C11.4572 10.0912 13.113 8.78637 14.1 8.04612L12.9 6.44612Z'
        fill='currentColor'
      />
    </svg>
  )
}

type StoryListProps = {
  stories: Array<{ id: string; title: string; slug?: string | null }>
  collectionSlug: string
  type: 'topic' | 'category'
}

function StoryList({ stories, collectionSlug, type }: StoryListProps) {
  return (
    <div className={style.accordionContent}>
      <div className={style.accordionDivider}></div>
      <ol className={style.storyList}>
        {stories.map((story, index) => (
          <StoryListItem key={story.id} story={story} index={index} collectionSlug={collectionSlug} type={type} />
        ))}
      </ol>
    </div>
  )
}

type CollectionCardProps = {
  item: CollectionItem
  type: 'topic' | 'category'
  countLabel: string
  isAccordionOpen: boolean
  onToggleAccordion: () => void
}

function CollectionCard({ item, type, countLabel, isAccordionOpen, onToggleAccordion }: CollectionCardProps) {
  return (
    <div className={style.collectionCard}>
      <div className={style.collectionLink}>
        <div className={style.collectionHeader}>
          <h3 className={[globalStyle.heading2, style.collectionName].join(' ')}>{item.name}</h3>
          <Link
            to={`/${type}/${item.slug || ''}`}
            className={[globalStyle.button_parent].join(' ')}
            tabIndex={-1}
          >
            <button
              className={[globalStyle.button, globalStyle.buttonSM, globalStyle.light].join(' ')}
            >
              Start
            </button>
          </Link>
        </div>
        <div className={style.storyCountContainer}>
          <p
            className={[globalStyle.subtitle, style.storyCount].join(' ')}
            onClick={(e) => {
              e.preventDefault()
              onToggleAccordion()
            }}
          >
            {item.stories?.length || 0} {countLabel}
          </p>
          <button
            className={[
              style.accordionButton,
              isAccordionOpen ? style.accordionButtonOpen : style.accordionButtonClosed,
            ].join(' ')}
            onClick={(e) => {
              e.preventDefault()
              onToggleAccordion()
            }}
            aria-expanded={isAccordionOpen}
          >
            <MoreIcon isOpen={isAccordionOpen} />
          </button>
        </div>
      </div>
      {isAccordionOpen && item.stories && item.stories.length > 0 && (
        <StoryList stories={item.stories} collectionSlug={item.slug || ''} type={type} />
      )}
    </div>
  )
}

type TabNavigationProps = {
  activeTab: 'topics' | 'categories'
  onTabChange: (tab: 'topics' | 'categories') => void
}

function TabNavigation({ activeTab, onTabChange }: TabNavigationProps) {
  return (
    <div style={{ 
      display: 'flex',
      gap: '10px',
      marginBottom: 'var(--space-l)',
    }}>
      <button
        className={[
          globalStyle.button,
          globalStyle.buttonSM,
          globalStyle.gray1,
          style.tabButton,
          activeTab === 'topics' ? style.active : '',
        ].join(' ')}
        onClick={() => onTabChange('topics')}
      >
        By topic
      </button>
      <button
        className={[
          globalStyle.button,
          globalStyle.buttonSM,
          globalStyle.gray1,
          style.tabButton,
          activeTab === 'categories' ? style.active : '',
        ].join(' ')}
        onClick={() => onTabChange('categories')}
      >
        By category
      </button>
    </div>
  )
}

type CollectionGridProps = {
  items: CollectionItem[]
  type: 'topic' | 'category'
  countLabel: string
  openAccordion: string | null
  onToggleAccordion: (id: string) => void
}

function CollectionGrid({ items, type, countLabel, openAccordion, onToggleAccordion }: CollectionGridProps) {
  if (!items || items.length === 0) {
    return <p className={globalStyle.bodySM}>No {type === 'topic' ? 'topics' : 'categories'} available.</p>
  }

  const rows = []
  for (let i = 0; i < items.length; i += 2) {
    rows.push(
      <div key={`row-${i}`} className={style.collectionRow}>
        <CollectionCard
          item={items[i]}
          type={type}
          countLabel={countLabel}
          isAccordionOpen={openAccordion === `${type}-${items[i].id}`}
          onToggleAccordion={() => onToggleAccordion(`${type}-${items[i].id}`)}
        />
        {items[i + 1] && (
          <CollectionCard
            item={items[i + 1]}
            type={type}
            countLabel={countLabel}
            isAccordionOpen={openAccordion === `${type}-${items[i + 1].id}`}
            onToggleAccordion={() => onToggleAccordion(`${type}-${items[i + 1].id}`)}
          />
        )}
      </div>
    )
  }

  return (
    <div className={style.collectionGrid}>
      {rows}
    </div>
  )
}

export function Collections({ data }: { data: CollectionsQuery | null }) {
  const [activeTab, setActiveTab] = useState<'topics' | 'categories'>('topics')
  const [openAccordion, setOpenAccordion] = useState<string | null>(null)

  if (!data) {
    return null;
  }

  const { topics, categories } = data


  // Filter out hidden categories
  const visibleCategories = categories?.filter(category => !category.hidden) || []

  const toggleAccordion = (id: string) => {
    setOpenAccordion(prev => prev === id ? null : id)
  }

  return (
    <div className={[style.collectionsWrapper].join(' ')}>
      <div>
        <h1 className={[globalStyle.heading1, style.header].join(' ')}>Start with a collection</h1>
        <div className={globalStyle.bodyLG}>Select a relevant topic and start editing away</div>
      </div>

      <div>
        <div className={style.collectionsContainer}>
          <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} />

          <div className={style.tabContent}>
            {activeTab === 'topics' && (
              <section className={style.collectionSection}>
                <CollectionGrid
                  items={topics || []}
                  type='topic'
                  countLabel='excerpts'
                  openAccordion={openAccordion}
                  onToggleAccordion={toggleAccordion}
                />
              </section>
            )}

            {activeTab === 'categories' && (
              <section className={style.collectionSection}>
                <CollectionGrid
                  items={visibleCategories}
                  type='category'
                  countLabel='excerpts'
                  openAccordion={openAccordion}
                  onToggleAccordion={toggleAccordion}
                />
              </section>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

